import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

interface Mission {
  id: string;
  title: string;
  description: string;
  iconUrl: string | null;
  actionType: string;
  requiredCount: number;
  pointsReward: number;
  progress: {
    currentCount: number;
    completed: boolean;
    pointsAwarded: boolean;
  } | null;
}

interface MissionsPayload {
  total: number;
  completed: number;
  missions: Mission[];
}

interface LoyaltyMissionsProps {
  title?: string;
  showResetTimer?: boolean;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127919;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view daily missions
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to complete missions and earn bonus points.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

const ACTION_ICONS: Record<string, string> = {
  read_article: '\uD83D\uDCF0',
  social_share: '\uD83D\uDCE4',
  visit_page: '\uD83D\uDCBB',
  purchase: '\uD83D\uDED2',
  checkin: '\uD83D\uDCC5',
  quiz: '\uD83E\uDDE0',
  steps: '\uD83D\uDEB6',
  comment: '\uD83D\uDCAC',
  referral: '\uD83D\uDC65',
  poll: '\uD83D\uDCCA',
};

function useResetTimer(): string {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    function calc() {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${h}h ${m}m`);
    }
    calc();
    const interval = setInterval(calc, 60000);
    return () => clearInterval(interval);
  }, []);

  return timeLeft;
}

export function LoyaltyMissionsBlock({ props }: { props: LoyaltyMissionsProps }) {
  const { isAuthenticated } = useAuth();
  const { data, loading: loyaltyLoading } = useLoyalty();
  const title = props.title ?? 'Daily Missions';
  const showResetTimer = props.showResetTimer ?? true;
  const resetTime = useResetTimer();

  const [missionsData, setMissionsData] = useState<MissionsPayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    const pid = data?.programId;
    if (!pid) return;
    apiGet<MissionsPayload>(`/customer/loyalty/missions?programId=${pid}`)
      .then((res) => { if (!cancelled) setMissionsData(res); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, data?.programId]);

  if (loyaltyLoading || loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-6 w-40 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="h-3 w-full bg-[var(--color-surface-border)] rounded mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-[var(--color-surface-border)] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const missions = missionsData ?? { total: 0, completed: 0, missions: [] };
  const completionPct = missions.total > 0
    ? Math.round((missions.completed / missions.total) * 100)
    : 0;

  return (
    <div className="loyalty-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)]">{title}</h2>
        <span className="text-2xl font-bold text-[var(--color-primary)]">{completionPct}%</span>
      </div>
      <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider mb-3">Total Progress</p>

      {/* Overall progress bar */}
      <div className="loyalty-progress-bar h-2.5 rounded-full overflow-hidden bg-[var(--color-surface-border)] mb-2">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${completionPct}%`, backgroundColor: 'var(--color-primary)' }}
        />
      </div>

      {/* Reset timer */}
      {showResetTimer && (
        <p className="text-xs text-[var(--color-text-muted)] mb-5">
          Resets in {resetTime}
        </p>
      )}

      {/* Mission list */}
      <div className="space-y-3">
        {missions.missions.map((mission) => {
          const completed = mission.progress?.completed ?? false;
          const currentCount = mission.progress?.currentCount ?? 0;
          const missionPct = mission.requiredCount > 0
            ? Math.min(100, Math.round((currentCount / mission.requiredCount) * 100))
            : 0;

          return (
            <div
              key={mission.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                completed
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                  : 'border-[var(--color-surface-border)] bg-[var(--color-surface)]'
              }`}
            >
              {/* Icon */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                style={{
                  backgroundColor: completed
                    ? 'var(--color-primary)'
                    : 'var(--color-surface-border)',
                }}
              >
                {mission.iconUrl ? (
                  <img src={mission.iconUrl} alt="" className="w-6 h-6 object-contain" />
                ) : completed ? (
                  <span className="text-white">&#10003;</span>
                ) : (
                  <span>{ACTION_ICONS[mission.actionType] ?? '\uD83C\uDFAF'}</span>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className={`text-sm font-semibold truncate ${completed ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
                    {mission.title}
                  </h3>
                  <span className="text-xs font-bold text-[var(--color-primary)] whitespace-nowrap">
                    +{mission.pointsReward} PTS
                  </span>
                </div>
                {!completed && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="loyalty-progress-bar flex-1 h-1.5 rounded-full overflow-hidden bg-[var(--color-surface-border)]">
                      <div
                        className="h-full rounded-full bg-[var(--color-primary)] transition-all"
                        style={{ width: `${missionPct}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-[var(--color-text-muted)] whitespace-nowrap">
                      {currentCount} / {mission.requiredCount}
                    </span>
                  </div>
                )}
              </div>

              {/* Action / Status */}
              <div className="flex-shrink-0">
                {completed ? (
                  <span className="text-[var(--color-primary)] text-lg">&#10003;</span>
                ) : mission.actionType === 'social_share' ? (
                  <button className="text-xs font-semibold px-3 py-1 rounded-full bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity">
                    Share
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {missions.missions.length === 0 && (
        <div className="text-center text-[var(--color-text-muted)] text-sm py-6">
          No missions available today. Check back tomorrow!
        </div>
      )}
    </div>
  );
}
